import { Badge, Button, IconTile, Spinner, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Lock, MessageCircle, Star } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { chatListSessionsQueryKey, chatUpdateSessionMutation, errorMessage } from "@/api";
import { useAreaCrumbs } from "@/components/areaCrumbs";
import { EmptyState } from "@/components/build/EmptyState";
import { EntityListPage } from "@/components/build/EntityListPage";
import { EntityRow } from "@/components/build/EntityRow";
import { useSpaceTrail } from "@/features/spaces/spaceCrumb";
import { formatRelativeTime } from "@/i18n/format";
import { type ChatHost, chatHost } from "../chatHost";
import { useDeleteChat } from "../useDeleteChat";
import { type ChatRowData, type ChatsFilter, useChatHistory } from "./useChatHistory";

/**
 * `/chats`: your chat history (all / starred) and chats shared with you.
 * With `space`, the same list for one space's chats (no "Shared with me"; new chats start there).
 * With the build host, the Build with AI history (`/build/with-ai/history`): only build chats, no "Shared with me".
 */
export function ChatsListPage({
  space,
  host = chatHost,
}: Readonly<{ space?: { uuid: string; name: string; title: string; subtitle: string }; host?: ChatHost }>) {
  const { t } = useTranslation("chat", { keyPrefix: "history" });
  const { t: tb } = useTranslation("buildChat", { keyPrefix: "history" });
  const isBuild = host.kind === "build";
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<ChatsFilter>("all");
  const areas = useAreaCrumbs();
  const spaceTrail = useSpaceTrail(space?.uuid, space?.name);
  const trail = isBuild ? [areas.build] : spaceTrail;
  const history = useChatHistory(filter, search.trim(), space?.uuid, host.source);
  const deleteChat = useDeleteChat(() => {});
  const star = useMutation({
    ...chatUpdateSessionMutation(),
    onSettled: () => queryClient.invalidateQueries({ queryKey: chatListSessionsQueryKey() }),
    onError: (error) => toast.error(errorMessage(error, t("starFailed"))),
  });

  const name = (row: ChatRowData) => row.title || t("untitled");
  const description = (row: ChatRowData) =>
    row.sharedBy
      ? t("sharedBy", { name: row.sharedBy, when: formatRelativeTime(row.updatedAt) })
      : t("updated", { when: formatRelativeTime(row.updatedAt), count: row.messageCount });
  const empty =
    isBuild && filter === "all"
      ? { title: tb("empty.title"), body: tb("empty.body") }
      : { title: t(`empty.${filter}.title`), body: t(`empty.${filter}.body`) };

  return (
    <EntityListPage
      title={space?.title ?? (isBuild ? tb("title") : t("title"))}
      trail={trail}
      subtitle={space?.subtitle ?? (isBuild ? tb("subtitle") : t("subtitle"))}
      newLabel={isBuild ? tb("new") : t("new")}
      onNew={() => navigate(space ? `/chat?space=${space.uuid}` : host.newPath)}
      search={search}
      onSearch={setSearch}
      filter={{
        label: t("filterLabel"),
        value: filter,
        onChange: (value) => setFilter(value as ChatsFilter),
        options: [
          { value: "all", label: t("filters.all") },
          { value: "starred", label: t("filters.starred") },
          ...(space || isBuild ? [] : [{ value: "shared", label: t("filters.shared") }]),
        ],
      }}
      footer={
        history.hasMore && (
          <Button variant="secondary" loading={history.loadingMore} onClick={history.loadMore}>
            {t("loadMore")}
          </Button>
        )
      }
    >
      {history.isPending && (
        <div className="flex justify-center py-12">
          <Spinner size="md" aria-label={t("loading")} />
        </div>
      )}
      {history.rows.map((row) => (
        <EntityRow
          key={row.uuid}
          icon={
            <IconTile>
              <MessageCircle size={18} />
            </IconTile>
          }
          name={name(row)}
          description={description(row)}
          meta={
            <>
              {!space && !isBuild && <SpaceBadge row={row} />}
              {row.isStarred && (
                <Badge variant="neutral" icon={<Star size={12} />}>
                  {t("starred")}
                </Badge>
              )}
            </>
          }
          onOpen={() => navigate(host.pathFor(row.uuid))}
          menuItems={
            row.sharedBy
              ? []
              : [
                  {
                    label: row.isStarred ? t("unstar") : t("star"),
                    onSelect: () =>
                      star.mutate({ path: { session_uuid: row.uuid }, body: { isStarred: !row.isStarred } }),
                  },
                  {
                    label: t("delete"),
                    danger: true,
                    onSelect: () => deleteChat.onDelete({ uuid: row.uuid, name: name(row) }),
                  },
                ]
          }
        />
      ))}
      {!history.isPending &&
        history.rows.length === 0 &&
        (search ? (
          <EmptyState title={t("noResultsTitle")} body={t("noResultsBody", { query: search })} />
        ) : (
          <EmptyState icon={<MessageCircle size={32} />} title={empty.title} body={empty.body} />
        ))}
      {deleteChat.dialog}
    </EntityListPage>
  );
}

/** The space a chat belongs to, on the all-chats list. */
function SpaceBadge({ row }: Readonly<{ row: ChatRowData }>) {
  const { t } = useTranslation("chat", { keyPrefix: "history" });
  if (row.spaceIsPersonal) {
    return (
      <Badge variant="neutral" icon={<Lock size={12} />}>
        {t("privateSpace")}
      </Badge>
    );
  }
  if (!row.spaceName) return null;
  return (
    <span className="hidden max-w-48 truncate md:inline-flex" title={row.spaceName}>
      <Badge variant="neutral">{row.spaceName}</Badge>
    </span>
  );
}
